import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateInventoryOwner } from '../../../shared/models/api/inventory-owner.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryOwnerService } from '../../../shared/services/api/inventory-owner.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { CreateInventoryOwnerFormValue } from './inventory-owner-create.models';

@Component({
  selector: 'opa-inventory-owner-create',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-owner-create.component.html',
  styleUrl: './inventory-owner-create.component.scss',
})
export class InventoryOwnerCreateComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryOwnerService = inject(InventoryOwnerService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryOwnerCreateComponent>): void {
    if (changes.open && this.open) this.form.reset();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const owner = this.formValueToOwner(formValue);
    this.subSink.sink = this.inventoryOwnerService
      .createInventoryOwner(owner)
      .pipe(
        tap({
          next: (createdOwner) => {
            this.toastManager.addMessage({
              text: `Owner '${createdOwner.description}' created`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateInventoryOwnerFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateInventoryOwnerFormValue>>({
      name: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.isNormalized(),
      ]),
      description: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToOwner(form: CreateInventoryOwnerFormValue): CreateInventoryOwner {
    const owner: CreateInventoryOwner = {
      description: form.description!,
      name: form.name!,
    };
    return owner;
  }
}
